import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import dayjs from 'dayjs'
import Loading from '../../../../../molecules/Loading'
import EmptySection from '../../../../../atoms/EmptySection'
import SydButton from '../../../../../commonDesign/Button'
import OperationalTable from '../../../../../organisms/OperationalTable'
import SydActionMenu from '../../../../../commonDesign/SydActionMenu'
import SydModal from '../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { useListListeners, useDeleteListener } from '../../../../../../api/listeners'
import { decipherError } from '../../../../../../utils/decipherError'
import { WorkflowToolbarContext } from '../WorkflowManagement'
import ListenerDialog from './ListenerDialog'

function DurableListenerList () {
  const { data: listeners, isLoading } = useListListeners({ isEphemeral: false })
  const { mutateAsync: deleteListener } = useDeleteListener()
  const dialogRef = useRef(null)
  const toolbarNode = useContext(WorkflowToolbarContext)

  const [confirmTarget, setConfirmTarget] = useState(null)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [confirmError, setConfirmError] = useState(null)

  const handleCreate = useCallback(() => {
    dialogRef.current?.open()
  }, [])

  const handleEdit = useCallback((listener) => {
    dialogRef.current?.open(listener)
  }, [])

  const handleDelete = useCallback((listener) => {
    setConfirmTarget(listener)
  }, [])

  // Intentionally does not reset confirmLoading — if the user dismisses via
  // backdrop/escape while a delete is in-flight, we avoid masking the pending
  // request. A follow-up could add disableBackdropClick while loading.
  const closeConfirm = useCallback(() => {
    setConfirmTarget(null)
    setConfirmError(null)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmTarget) return
    setConfirmLoading(true)
    setConfirmError(null)
    try {
      await deleteListener({ listenerId: confirmTarget.listenerId })
      closeConfirm()
    } catch (err) {
      setConfirmError(decipherError(err))
    } finally {
      setConfirmLoading(false)
    }
  }, [confirmTarget, deleteListener, closeConfirm])

  const columns = useMemo(() => [
    { id: 'eventType', accessor: 'eventType', Header: 'Event Type' },
    { id: 'dagId', accessor: 'dagId', Header: 'DAG ID' },
    { id: 'dagStepId', accessor: 'dagStepId', Header: 'Step ID' },
    {
      id: 'isAsync',
      accessor: 'isAsync',
      Header: 'Async',
      Cell: ({ value }) => value ? 'Yes' : 'No' // eslint-disable-line react/prop-types
    },
    { id: 'completeOn', accessor: 'completeOn', Header: 'Complete On' },
    { id: 'failOn', accessor: 'failOn', Header: 'Fail On' },
    {
      id: 'createdDate',
      accessor: 'createdDate',
      Header: 'Created',
      Cell: ({ value }) => value ? dayjs(value).format('MMM D, YYYY h:mm A') : '\u2014' // eslint-disable-line react/prop-types
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      /* eslint-disable react/prop-types */
      Cell: ({ row }) => {
        const listener = row.original
        const actions = [
          { key: 'edit', label: 'Edit', icon: 'edit', onClick: () => handleEdit(listener) },
          { key: 'delete', label: 'Delete', icon: 'delete', onClick: () => handleDelete(listener) }
        ]
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <SydActionMenu actions={actions} />
          </div>
        )
      }
      /* eslint-enable react/prop-types */
    }
  ], [handleEdit, handleDelete])

  if (isLoading) return <Loading />

  return (
    <>
      {toolbarNode && createPortal(
        <SydButton size='sm' variant='primary' icon='add' onClick={handleCreate}>
          Add Listener
        </SydButton>,
        toolbarNode
      )}
      {!listeners?.length ? (
        <EmptySection
          title='No Durable Listeners'
          description='Create a durable listener to trigger a workflow when a specific event is received.'
        />
      ) : (
        <OperationalTable
          variant='v2'
          columns={columns}
          data={listeners}
          defaultSort={[{ id: 'eventType', desc: false }]}
        />
      )}

      <ListenerDialog ref={dialogRef} />

      <SydModal
        open={!!confirmTarget}
        onClose={closeConfirm}
        title='Delete Listener'
      >
        <p>
          Are you sure you want to delete the listener for event{' '}
          <strong>{confirmTarget?.eventType}</strong>? This cannot be undone.
        </p>
        {confirmError ? <p style={{ color: 'var(--error)', marginTop: 8 }}>{confirmError}</p> : null}
        <SydModalActions>
          <SydButton disabled={confirmLoading} variant='ghost' size='md' onClick={closeConfirm}>
            Cancel
          </SydButton>
          <SydButton
            processing={confirmLoading}
            variant='primary'
            priority='warning'
            icon='delete'
            size='md'
            onClick={handleConfirmDelete}
          >
            Delete
          </SydButton>
        </SydModalActions>
      </SydModal>
    </>
  )
}

export default DurableListenerList
